import { isAfter, isWithinInterval } from "date-fns";
import { canAddSalesForKind, isNotClosed, isSaleBlockedByClosedPeriod } from "helpers/channelCapabilities";
import { getCleanStartorEndDay } from "helpers/utils";
import type { ChannelKind } from "store/types";

type PeriodLike = {
    id: number;
    isActive: number | boolean | string;
    periodStart: Date | string;
    periodEnd: Date | string;
};

export type AddSaleDateStatus = "error" | "warning" | "warning-otherPeriod" | "warning-otherPeriod-plural" | undefined;

const isDateInPeriod = (date: Date, period: PeriodLike): boolean =>
    isWithinInterval(date, {
        start: getCleanStartorEndDay(period.periodStart),
        end: getCleanStartorEndDay(period.periodEnd),
    });

/**
 * Date status for the add-sale modal.
 * Non-CM: error when the date matches only closed periods.
 * Overlap (closed + open) is allowed — warning if it is not the current period.
 * CM: closed periods are allowed.
 */
export const getAddSaleDateStatus = ({
    periods,
    chosenDate,
    currentPeriod,
    channelKind,
}: {
    periods: PeriodLike[];
    chosenDate: Date | string;
    currentPeriod: PeriodLike;
    channelKind: ChannelKind;
}): AddSaleDateStatus => {
    const sanitizedChosenDate = getCleanStartorEndDay(chosenDate);
    const matchingPeriods = periods.filter((period) => isDateInPeriod(sanitizedChosenDate, period));

    if (isSaleBlockedByClosedPeriod(matchingPeriods, channelKind)) {
        return "error";
    }

    const openMatching = matchingPeriods.filter((period) => isNotClosed(period.isActive));
    const openPeriods = periods.filter((period) => isNotClosed(period.isActive));

    if (matchingPeriods.length === 0) {
        const inFuture =
            openPeriods.length > 0 &&
            openPeriods.every((period) => isAfter(sanitizedChosenDate, getCleanStartorEndDay(period.periodEnd)));
        return canAddSalesForKind(channelKind, true) && inFuture ? "warning" : "error";
    }

    const isDifferentActivePeriod = !isDateInPeriod(sanitizedChosenDate, currentPeriod);
    if (isDifferentActivePeriod && openMatching.length > 0) {
        return openMatching.length > 1 ? "warning-otherPeriod-plural" : "warning-otherPeriod";
    }

    return undefined;
};
